<template>
	<Page animate>
		<template v-slot:content>
			<SafeAreaTop @close="onClose">
				<div class="container">
					<div class="title">Step-by-step guides to kick-start your wellness journey</div>
					<div class="subtitle">Exclusive offer to help you see the results you want</div>

					<div class="workbooks">
						<Workbook
							class="mb-40"
							:class="{ active: product.active }"
							v-for="product in products"
							:key="product.id"
							:title="product.text"
							:subtitle="product.subtitle"
							:image="product.image"
							:price="product.price"
						/>

						<div class="price">One-time offer: {{ active_product.price }}</div>
						<div class="old-price">
							Regular price: {{ active_product.old.price }}. Save {{ active_product.old.discount }}%!
						</div>
						<div class="hr"></div>
						<div class="note">Keep the workbooks even if you unsubscribe later. No strings attached!</div>
					</div>
				</div>
			</SafeAreaTop>

			<modal :isOpen="showDownloadModal">
				<template v-slot:body>
					<DownloadModal @close="onCloseDownloadModal" :user_id="user_id" />
				</template>
			</modal>
		</template>

		<template v-slot:footer>
			<div class="container">
				<p-button @click.prevent="handleSubmit" label="" :loading="isLoading" cls="btn-primary btn-block">
					Continue
				</p-button>
				<div class="footer-note">Workbooks are not included in the subscription price</div>
			</div>
		</template>
	</Page>
</template>

<script>
import Page from '@/components/Page'
import PButton from '@/components/Button'
import SafeAreaTop from '@/components/SafeAreaTop.vue'
import Workbook from '@/components/Workbook.vue'
import Modal from '@/components/Modal'
import DownloadModal from '@/components/DownloadModal'
import { useStore } from 'vuex'
import { computed, inject, ref, watch } from '@vue/runtime-core'
import useApi from '@/composition/api.js'
import useConfig from '@/composition/config.js'
import useEnv from '@/composition/env.js'

export default {
	components: {
		Page,
		PButton,
		SafeAreaTop,
		Workbook,
		Modal,
		DownloadModal
	},

	setup() {
		const store = useStore()
		const api = useApi()
		const amplitude = inject('amplitude')
		const appboy = inject('appboy')
		const config = useConfig()
		const env = useEnv()

		let isLoading = computed(() => store.state.isLoading)

		/** Email */
		let hasEmail = computed(() => Boolean(store.state.user.email))

		/** Download Modal */
		let showDownloadModal = ref(false)

		let user_id = computed(() => store.state.user.user_id)

		const products = computed(() =>
			store.getters['products/getData']([
				{
					active: false,
					id: 'simple_nutrition_workbook',
					text: 'Nutrition Workbook',
					subtitle: 'for sustainable weight loss',
					image: require('@/assets/post/nutrition/en2.png'),
					price: '[old_price]',
					old: {
						id: 'simple_full_price_subscription_12_months'
					}
				},
				{
					active: false,
					id: 'simple_workout_workbook',
					text: 'Workout Workbook',
					subtitle: 'to accompany your wellness journey',
					image: require('@/assets/post/workout/en2.png'),
					price: '[old_price]',
					old: {
						id: 'simple_full_price_subscription_12_months'
					}
				},
				{
					active: true,
					id: 'simple_all_workbooks',
					text: 'Workbook Bundle',
					subtitle: 'Nutrition Workbook + Workout Workbook',
					image: require('@/assets/post/workbooks/en2.png'),
					price: '[price]',
					old: {
						id: 'simple_original_subscription_12_months_trial'
					}
				}
			])
		)

		let active_product = ref(products.value[2])
		watch(products, () => {
			active_product.value = products.value[2]
		})

		const close = (Status = 'Close') => {
			amplitude.logEvent('Post Paywall - Close', {
				Layout: config.get({ key: 'layout', default_value: 'Workbooks V2' }),
				From: store.getters['getSource'],
				Step: 'Workbooks',
				Status
			})
			api.close()
		}

		const onClose = () => close()
		const onCloseDownloadModal = () => close('Purchase')

		/** Purchase */
		const Purchase = async () => {
			api.purchase({
				productId: active_product.value.id,
				onFinish: async () => {
					if (hasEmail) {
						const options = {
							external_id: user_id.value,
							has_workout_workbook: true,
							has_nutrition_workbook: true
						}

						await appboy.userTrack([options])
						await appboy.canvas(env('BRAZE_WORKBOOKS_CANVAS_ID'), user_id.value)
					}

					amplitude.logEvent('Paywall - Done', {
						Layout: config.get({ key: 'layout', default_value: 'Workbooks V2' }),
						From: store.getters['getSource'],
						Step: 'Workbooks',
						ProductId: active_product.value.id,
						Status: 'Purchase'
					})

					showDownloadModal.value = true
				},
				onError: (product, text) => {
					console.log(product, text)
				},
				onCancel: () => {},
				closeOnSuccess: false
			})
		}

		const handleSubmit = () => {
			Purchase(active_product.value)
		}

		return {
			isLoading,
			handleSubmit,
			active_product,
			onClose,
			onCloseDownloadModal,
			showDownloadModal,
			user_id,
			products
		}
	}
}
</script>

<style lang="scss" scoped>
.title {
	font-size: 1.4rem;
	font-weight: bold;
	text-align: center;
	padding: 0 1.5rem;
	line-height: 1.3;
	padding-top: 7vh;
}

.subtitle {
	font-size: 1.16rem;
	color: #3b3c57;
	text-align: center;
	padding: 0 1.5rem;
	line-height: 1.3;
	margin: 0.6rem;
}

.workbooks {
	background: #ffffff;
	box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.15);
	border-radius: 1rem;
	padding: 0.8rem;
	margin-top: 1.6rem;

	.hr {
		margin: 1rem 0;
		border-bottom: 1px solid #f0f0f3;
	}
}

.plus {
	padding: 0.5rem 0;
	svg {
		display: block;
		margin-left: auto;
		margin-right: 2rem;
	}
}

.price {
	font-weight: bold;
	text-align: center;
	color: #eca58f;
	font-size: 1.5rem;
	margin-top: -1rem;
}

.old-price {
	font-weight: bold;
	text-align: center;
	font-size: 1.1rem;
}

.note {
	padding-bottom: 0.7rem;
	text-align: center;
	font-size: 1.1rem;
	max-width: 90%;
	margin-left: auto;
	margin-right: auto;
	line-height: 1.3;
}

.footer-note {
	text-align: center;
	margin-top: 0.9rem;
	font-size: 0.9rem;
}
</style>
